import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardMedia,
  Container,
  Divider,
  Grid,
  IconButton,
  Link,
  Rating,
  Snackbar,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import FavoriteIcon from "@mui/icons-material/Favorite";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import ArrowBackIosNewRounded from "@mui/icons-material/ArrowBackIosNewRounded";
import ArrowForwardIosRounded from "@mui/icons-material/ArrowForwardIosRounded";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import BookingForm from "../BookingForm/BookingForm";
import RelatedProducts from "../RelatedProduct/RelatedProduct";
import PopularThisWeek from "../PopularThisWeek/Popular";
import ProductReview from "../ProductReview/ProductReview";
import Navbar from "../Navbar/Navbar";
import Footer from "../Footer/Footer";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import config from "../../Config";
import { PropagateLoader } from "react-spinners";
import PopUp from "../Pop-up/pop-up";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import CommonShareDialog from "../CommonShareable/CommonShareDialog";
import Faq from "../Faq/Faq";

const StyledToggleButton = styled(ToggleButton)(() => ({
  minWidth: 48,
  height: 44,
  padding: "0 16px",
  border: "1px solid #d7d0c6 !important",
  borderRadius: "0 !important",
  color: "#171717",
  background: "#fff",
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: "0.08em",
  "&.Mui-selected": {
    backgroundColor: "#171717",
    color: "#fff",
    borderColor: "#171717 !important",
  },
  "&.Mui-selected:hover": {
    backgroundColor: "#977743",
    borderColor: "#977743 !important",
  },
  "&:hover": {
    backgroundColor: "#f1ede7",
  },
}));

const ThumbnailImage = styled(CardMedia)(({ selected }) => ({
  width: 76,
  height: 92,
  cursor: "pointer",
  objectFit: "cover",
  background: "#e9e4dc",
  border: selected ? "2px solid #171717" : "1px solid #ddd7ce",
  opacity: selected ? 1 : 0.72,
  transition: "all 0.25s ease",
  flexShrink: 0,
  "&:hover": {
    opacity: 1,
  },
}));

export default function ProductDetail() {
  const [shareOpen, setShareOpen] = useState(false);
  const { mainCat, subCat, productId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [productData, setProductData] = useState(null);
  const [tryOnDate, setTryOnDate] = useState(null);
  const [productReview, setProductReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [size, setSize] = useState("");
  const [selectedImage, setSelectedImage] = useState(0);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [wishlistMessage, setWishlistMessage] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [wishlistSeverity, setWishlistSeverity] = useState("success");
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [bookingType, setBookingType] = useState("rental");
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.key]);

  useEffect(() => {
    const fetchProductData = async () => {
      try {
        setLoading(true);

        const apiUrl = `${config.apiBaseUrl}/${config.getProductDetail}`;

        const response = await axios.get(apiUrl, {
          headers: {
            "product-id": productId,
          },
        });

        const data = response?.data;

        const productDetails = data?.ProductData || null;

        setProductData(productDetails);
        setIsWishlisted(Boolean(data?.isInWishlist));
        setTryOnDate(data?.tryOnDate || null);
        setProductReview(
          data?.productReview || {
            totalRating: 0,
            averageRating: 0,
            totalStar: 0,
            starCounts: {
              1: 0,
              2: 0,
              3: 0,
              4: 0,
              5: 0,
            },
          },
        );

        setSize(productDetails?.product?.size || "");
        setSelectedImage(0);
      } catch (error) {
        console.error("Error fetching product data:", error);
        setProductData(null);
      } finally {
        setLoading(false);
      }
    };

    if (productId) {
      fetchProductData();
    }
  }, [productId]);

  const product = productData?.product || {};
  const owner = productData?.owner || {};
  const slots = productData?.slots || {};
  const address = productData?.address || {};

  const productImages = Array.isArray(productData?.productImage)
    ? productData.productImage.filter(Boolean)
    : [];

  const sizeOptions = useMemo(() => {
    const rawSize = product?.size;

    if (Array.isArray(rawSize)) {
      return rawSize.map((item) => String(item).trim()).filter(Boolean);
    }

    if (!rawSize) {
      return [];
    }

    const value = String(rawSize).trim();

    if (value.includes(",")) {
      return value
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
    }

    return [value];
  }, [product?.size]);

  const isMainCatAvailable = mainCat
    ? mainCat
      .replace(/-/g, " ")
      .replace(/\b\w/g, (letter) => letter.toUpperCase())
    : "";

  const isSubCatAvailable = subCat
    ? subCat
      .replace(/-/g, " ")
      .replace(/\b\w/g, (letter) => letter.toUpperCase())
    : "";

  const productName = product?.title || "Untitled Collection";

  const description =
    product?.description ||
    "A carefully selected piece from Hindustan Wardrobe.";

  const rentAmount = Number(product?.rentAmount || 0);

  const ownerName = owner?.name || owner?.userName || "Wardrobe Partner";

  const pickupAddress = [
    address?.street1,
    address?.street2,
    address?.city,
    address?.state,
    address?.country,
    address?.pinCode ? `- ${address.pinCode}` : "",
  ]
    .filter(Boolean)
    .join(", ")
    .replace(", -", " -");

  const availableDate = slots?.availableDate || "Please check availability";

  const tryOnBookingDate = tryOnDate?.bookingDate || null;

  const tryOnBookingTimes = Array.isArray(tryOnDate?.bookingTime)
    ? tryOnDate.bookingTime
    : [];

  const averageRating = Number(productReview?.averageRating || 0);

  const totalRating = Number(productReview?.totalRating || 0);

  const handleSizeChange = (_, newSize) => {
    if (newSize !== null) {
      setSize(newSize);
    }
  };

  const handleAddToWishlist = async () => {
    const userId = sessionStorage.getItem("userId");

    if (!userId) {
      setWishlistSeverity("warning");
      setWishlistMessage("Please log in to add to wishlist.");
      setOpenSnackbar(true);
      return;
    }

    try {
      const response = await axios.post(
        `${config.apiBaseUrl}/${config.addToWishlist}`,
        {
          userId,
          productId,
        },
      );

      let message =
        response.data?.message || "Product added to wishlist successfully.";

      let severity = "success";

      if (typeof response.data === "string") {
        message = response.data;
      }

      const normalizedMessage = message.toLowerCase();

      if (
        normalizedMessage.includes("already exist") ||
        normalizedMessage.includes("already exists")
      ) {
        severity = "info";
        setIsWishlisted(true);
      } else if (normalizedMessage.includes("success")) {
        setIsWishlisted(true);
      } else {
        severity = "error";
      }

      setWishlistSeverity(severity);
      setWishlistMessage(message);
      setOpenSnackbar(true);
    } catch (error) {
      let errorMessage = "An unexpected error occurred.";

      let severity = "error";

      if (error.response?.data) {
        errorMessage =
          typeof error.response.data === "string"
            ? error.response.data
            : error.response.data?.message || errorMessage;

        const normalizedMessage = errorMessage.toLowerCase();

        if (
          normalizedMessage.includes("already exist") ||
          normalizedMessage.includes("already exists")
        ) {
          severity = "info";
          setIsWishlisted(true);
        }
      }

      setWishlistSeverity(severity);
      setWishlistMessage(errorMessage);
      setOpenSnackbar(true);
    }
  };

  const handleOwnerClick = () => {
    if (owner?.userId) {
      navigate(`/ownerprofile/${owner.userId}`);
    }
  };

  const openAppRedirect = () => {
    const userId = sessionStorage.getItem("userId");
    const appPackage = "com.ytctechventures.hindustanwardrobe";

    if (!userId) {
      navigate("/Signin");
      return;
    }

    const intentUrl = `intent://ownerProfile?ownerId=${userId}#Intent;scheme=https;package=${appPackage};end`;

    window.location.href = intentUrl;

    setTimeout(() => {
      window.location.href = `https://play.google.com/store/apps/details?id=${appPackage}`;
    }, 1200);
  };

  const handlePreviousImage = () => {
    if (!productImages.length) {
      return;
    }

    setSelectedImage((previous) =>
      previous === 0 ? productImages.length - 1 : previous - 1,
    );
  };

  const handleNextImage = () => {
    if (!productImages.length) {
      return;
    }

    setSelectedImage((previous) =>
      previous === productImages.length - 1 ? 0 : previous + 1,
    );
  };

  const handleShareProduct = () => {
    setShareOpen(true);
  };
  const handleBookNow = () => {
    const token = sessionStorage.getItem("newToken");
    const userId = sessionStorage.getItem("userId");

    if (!token || !userId) {
      setShowLoginPopup(true);
      return;
    }

    setBookingType("rental");
    setShowBookingForm(true);
  };

  const handleBookTryOn = () => {
    const token = sessionStorage.getItem("newToken");
    const userId = sessionStorage.getItem("userId");

    if (!token || !userId) {
      setShowLoginPopup(true);
      return;
    }

    setBookingType("tryOn");
    setShowBookingForm(true);
  };

  const handleLoginPopupPrimary = () => {
    setShowLoginPopup(false);
    navigate("/Signin");
  };

  if (loading) {
    return (
      <>
        <Navbar />

        <Box
          sx={{
            minHeight: "70vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "#f7f4ef",
          }}
        >
          <Stack alignItems="center" spacing={2}>
            <PropagateLoader color="#977743" size={8} />

            <Typography
              sx={{
                fontFamily: "Georgia, serif",
                fontSize: 19,
                color: "#171717",
              }}
            >
              Preparing your selection...
            </Typography>
          </Stack>
        </Box>

        <Footer />
      </>
    );
  }

  if (!productData) {
    return (
      <>
        <Navbar />

        <Box
          sx={{
            minHeight: "65vh",
            background: "#f7f4ef",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            px: 3,
          }}
        >
          <Stack alignItems="center" spacing={2.5}>
            <Typography
              sx={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.22em",
                textTransform: "uppercase",
                color: "#977743",
              }}
            >
              Collection Update
            </Typography>

            <Typography
              sx={{
                fontFamily: "Georgia, serif",
                fontSize: {
                  xs: 38,
                  md: 55,
                },
              }}
            >
              Product unavailable
            </Typography>

            <Typography
              sx={{
                color: "#77716a",
                maxWidth: 480,
                lineHeight: 1.8,
              }}
            >
              This piece is currently unavailable or could not be found.
            </Typography>

            <Button
              onClick={() => navigate("/")}
              endIcon={<ArrowForwardIosRounded sx={{ fontSize: 12 }} />}
              sx={{
                mt: 1,
                minHeight: 50,
                px: 4,
                background: "#171717",
                color: "#fff",
                borderRadius: 0,
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.15em",
                textTransform: "uppercase",
                "&:hover": {
                  background: "#977743",
                },
              }}
            >
              Explore Collections
            </Button>
          </Stack>
        </Box>

        <Footer />
      </>
    );
  }

  const handleTryOnBooking = async (bookingData) => {
    try {
      const userId =
        sessionStorage.getItem("userId") || localStorage.getItem("userId");

      if (!userId) {
        throw new Error("User not logged in.");
      }

      const requestBody = {
        userId,
        productId: String(bookingData.productId),
        paymentId: bookingData.paymentId || null,
        bookingDate: bookingData.bookingDate,
        bookingType: bookingData.bookingType,
        bookingTime:
          bookingData.bookingType === "tryOn"
            ? bookingData.bookingTime
            : bookingData.bookingTime || null,
        deliveryOption:
          bookingData.bookingType === "tryOn"
            ? "pickup"
            : bookingData.deliveryOption || "delivery",
        address:
          bookingData.bookingType === "tryOn"
            ? null
            : typeof bookingData.address === "string"
              ? bookingData.address
              : JSON.stringify(bookingData.address || {}),
      };

      const response = await axios.post(
        `${config.apiBaseUrl}/${config.booNow}`,
        requestBody,
      );

      if (response.status !== 200 || !response.data?.bookingId) {
        throw new Error(response.data?.message || "Booking failed.");
      }

      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || error.message || "Booking failed.",
      );
    }
  };
  return (
    <>
      <Navbar />

      <Box
        sx={{
          background: "#f7f4ef",
          minHeight: "100vh",
          color: "#171717",
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            pt: {
              xs: 2,
              md: 4,
            },
            pb: {
              xs: 6,
              md: 10,
            },
          }}
        >
          <Breadcrumbs
            separator={
              <NavigateNextIcon
                sx={{
                  fontSize: 15,
                  color: "#aaa39a",
                }}
              />
            }
            sx={{
              mb: {
                xs: 3,
                md: 5,
              },
            }}
          >
            <Link
              component="button"
              underline="none"
              onClick={() => navigate("/")}
              sx={{
                border: 0,
                background: "transparent",
                padding: 0,
                color: "#77716a",
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                cursor: "pointer",
                "&:hover": {
                  color: "#977743",
                },
              }}
            >
              Home
            </Link>

            {isMainCatAvailable && (
              <Link
                component="button"
                underline="none"
                onClick={() =>
                  navigate(
                    `/category/${mainCat}/${subCat || ""}/${product.subCategory || ""}`,
                  )
                }
                sx={{
                  border: 0,
                  background: "transparent",
                  padding: 0,
                  color: "#77716a",
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  cursor: "pointer",
                  "&:hover": {
                    color: "#977743",
                  },
                }}
              >
                {isMainCatAvailable}
              </Link>
            )}

            {isSubCatAvailable && (
              <Link
                component="button"
                underline="none"
                onClick={() =>
                  navigate(
                    `/category/${mainCat}/${subCat}/${product.subCategory}`,
                  )
                }
                sx={{
                  border: 0,
                  background: "transparent",
                  padding: 0,
                  color: "#77716a",
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  cursor: "pointer",
                  "&:hover": {
                    color: "#977743",
                  },
                }}
              >
                {isSubCatAvailable}
              </Link>
            )}

            <Typography
              sx={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "#171717",
              }}
            >
              {productName}
            </Typography>
          </Breadcrumbs>

          {showBookingForm ? (
            <Box
              sx={{
                background: "#fff",
                border: "1px solid #ddd7ce",
                p: {
                  xs: 2,
                  md: 5,
                },
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 3 }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: "0.18em",
                      textTransform: "uppercase",
                      color: "#977743",
                      mb: 1,
                    }}
                  >
                    Booking
                  </Typography>

                  <Typography
                    sx={{
                      fontFamily: "Georgia, serif",
                      fontSize: 32,
                    }}
                  >
                    Reserve {productName}
                  </Typography>
                </Box>

                <Button
                  onClick={() => setShowBookingForm(false)}
                  sx={{
                    color: "#171717",
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                  }}
                >
                  Back
                </Button>
              </Stack>

              <BookingForm
                bookingType={bookingType}
                productId={productId}
                productData={productData}
                onBack={() => setShowBookingForm(false)}
                tryOnDateTimeUrl={`${config.apiBaseUrl}/${config.tryOnTime}`}
                onTryOnBooking={handleTryOnBooking}
              />
            </Box>
          ) : (
            <Grid
              container
              spacing={{
                xs: 4,
                md: 7,
              }}
            >
              <Grid item xs={12} md={7}>
                <Box
                  sx={{
                    position: "sticky",
                    top: 95,
                  }}
                >
                  <Card
                    elevation={0}
                    sx={{
                      background: "#e9e4dc",
                      position: "relative",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        position: "relative",
                        minHeight: {
                          xs: 430,
                          sm: 550,
                          md: 650,
                        },
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {productImages.length > 0 ? (
                        <CardMedia
                          component="img"
                          image={productImages[selectedImage]}
                          alt={productName}
                          sx={{
                            width: "100%",
                            height: {
                              xs: 430,
                              sm: 550,
                              md: 650,
                            },
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <Typography
                          sx={{
                            fontFamily: "Georgia, serif",
                            fontSize: 40,
                            color: "#8c8479",
                          }}
                        >
                          HW
                        </Typography>
                      )}

                      <Box
                        sx={{
                          position: "absolute",
                          top: 18,
                          left: 18,
                          px: 1.6,
                          py: 0.8,
                          background: "rgba(247,244,239,0.94)",
                          backdropFilter: "blur(8px)",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 9,
                            fontWeight: 700,
                            letterSpacing: "0.17em",
                            textTransform: "uppercase",
                          }}
                        >
                          Hindustan Wardrobe
                        </Typography>
                      </Box>

                      {/* Share Button */}
                      <IconButton
                        onClick={handleShareProduct}
                        aria-label="Share product"
                        sx={{
                          position: "absolute",
                          top: 16,
                          right: 70,
                          width: 46,
                          height: 46,
                          background: "rgba(247,244,239,0.94)",
                          color: "#171717",
                          "&:hover": {
                            background: "#fff",
                            color: "#977743",
                          },
                        }}
                      >
                        <ShareOutlinedIcon />
                      </IconButton>

                      {/* Wishlist Button */}
                      <IconButton
                        onClick={handleAddToWishlist}
                        aria-label="Add to wishlist"
                        sx={{
                          position: "absolute",
                          top: 16,
                          right: 16,
                          width: 46,
                          height: 46,
                          background: "rgba(247,244,239,0.94)",
                          color: isWishlisted ? "#977743" : "#171717",
                          "&:hover": {
                            background: "#fff",
                          },
                        }}
                      >
                        {isWishlisted ? (
                          <FavoriteIcon />
                        ) : (
                          <FavoriteBorderIcon />
                        )}
                      </IconButton>

                      {productImages.length > 1 && (
                        <>
                          <IconButton
                            onClick={handlePreviousImage}
                            sx={{
                              position: "absolute",
                              left: 16,
                              bottom: 16,
                              width: 42,
                              height: 42,
                              background: "rgba(247,244,239,0.94)",
                              color: "#171717",
                              "&:hover": {
                                background: "#fff",
                              },
                            }}
                          >
                            <ArrowBackIosNewRounded sx={{ fontSize: 15 }} />
                          </IconButton>

                          <IconButton
                            onClick={handleNextImage}
                            sx={{
                              position: "absolute",
                              left: 66,
                              bottom: 16,
                              width: 42,
                              height: 42,
                              background: "rgba(247,244,239,0.94)",
                              color: "#171717",
                              "&:hover": {
                                background: "#fff",
                              },
                            }}
                          >
                            <ArrowForwardIosRounded sx={{ fontSize: 15 }} />
                          </IconButton>
                        </>
                      )}
                    </Box>
                  </Card>

                  {productImages.length > 1 && (
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{
                        mt: 2,
                        overflowX: "auto",
                        pb: 1,
                      }}
                    >
                      {productImages.map((image, index) => (
                        <ThumbnailImage
                          key={`${image}-${index}`}
                          component="img"
                          image={image}
                          selected={selectedImage === index}
                          onClick={() => setSelectedImage(index)}
                          alt={`${productName} ${index + 1}`}
                        />
                      ))}
                    </Stack>
                  )}
                </Box>
              </Grid>

              <Grid item xs={12} md={5}>
                <Stack
                  spacing={{
                    xs: 3,
                    md: 3.5,
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: "0.22em",
                        textTransform: "uppercase",
                        color: "#977743",
                        mb: 1.5,
                      }}
                    >
                      Rental Collection
                    </Typography>

                    <Typography
                      component="h1"
                      sx={{
                        fontFamily: "Georgia, serif",
                        fontSize: {
                          xs: 40,
                          sm: 48,
                          md: 55,
                        },
                        lineHeight: 1.02,
                        fontWeight: 400,
                        letterSpacing: "-0.04em",
                        textTransform: "capitalize",
                      }}
                    >
                      {productName}
                    </Typography>

                    <Stack
                      direction="row"
                      alignItems="baseline"
                      spacing={1}
                      sx={{ mt: 2 }}
                    >
                      <Typography
                        sx={{
                          fontSize: 23,
                          fontWeight: 600,
                        }}
                      >
                        ₹{rentAmount.toLocaleString("en-IN")}
                      </Typography>

                      <Typography
                        sx={{
                          fontSize: 11,
                          color: "#77716a",
                          letterSpacing: "0.08em",
                          textTransform: "uppercase",
                        }}
                      >
                        per day
                      </Typography>
                    </Stack>

                    {totalRating > 0 && (
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{ mt: 1.5 }}
                      >
                        <Rating
                          value={averageRating}
                          precision={0.1}
                          readOnly
                          size="small"
                        />

                        <Typography
                          sx={{
                            fontSize: 11,
                            color: "#77716a",
                          }}
                        >
                          {averageRating.toFixed(1)} ({totalRating})
                        </Typography>
                      </Stack>
                    )}
                  </Box>

                  <Divider
                    sx={{
                      borderColor: "#ddd7ce",
                    }}
                  />

                  <Typography
                    sx={{
                      color: "#67625c",
                      fontSize: 14,
                      lineHeight: 1.9,
                    }}
                  >
                    {description}
                  </Typography>

                  <Box
                    sx={{
                      border: "1px solid #ddd7ce",
                      background: "#fff",
                      p: {
                        xs: 2,
                        md: 2.5,
                      },
                    }}
                  >
                    <Stack spacing={2}>
                      {/* <Stack
                        direction="row"
                        spacing={1.5}
                        alignItems="flex-start"
                      >
                        <CalendarMonthOutlinedIcon
                          sx={{
                            fontSize: 20,
                            color: "#977743",
                            mt: 0.2,
                          }}
                        />

                        <Box>
                          <Typography
                            sx={{
                              fontSize: 9,
                              fontWeight: 700,
                              letterSpacing: "0.16em",
                              textTransform: "uppercase",
                              color: "#77716a",
                              mb: 0.5,
                            }}
                          >
                            Rental Availability
                          </Typography>

                          <Typography
                            sx={{
                              fontSize: 13,
                              lineHeight: 1.5,
                            }}
                          >
                            {availableDate}
                          </Typography>
                        </Box>
                      </Stack> */}

                      <Divider
                        sx={{
                          borderColor: "#eee9e2",
                        }}
                      />

                      <Stack
                        direction="row"
                        spacing={1.5}
                        alignItems="flex-start"
                      >
                        <LocationOnOutlinedIcon
                          sx={{
                            fontSize: 20,
                            color: "#977743",
                            mt: 0.2,
                          }}
                        />

                        <Box>
                          <Typography
                            sx={{
                              fontSize: 9,
                              fontWeight: 700,
                              letterSpacing: "0.16em",
                              textTransform: "uppercase",
                              color: "#77716a",
                              mb: 0.5,
                            }}
                          >
                            Pickup Location
                          </Typography>

                          <Typography
                            sx={{
                              fontSize: 13,
                              lineHeight: 1.6,
                              color: "#4e4a45",
                            }}
                          >
                            {pickupAddress || "Location available on request"}
                          </Typography>
                        </Box>
                      </Stack>
                    </Stack>
                  </Box>

                  {sizeOptions.length > 0 && (
                    <Box>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ mb: 1.5 }}
                      >
                        <Typography
                          sx={{
                            fontSize: 10,
                            fontWeight: 700,
                            letterSpacing: "0.17em",
                            textTransform: "uppercase",
                          }}
                        >
                          Select Size
                        </Typography>

                        <Typography
                          sx={{
                            fontSize: 11,
                            color: "#977743",
                          }}
                        >
                          {size}
                        </Typography>
                      </Stack>

                      <ToggleButtonGroup
                        value={size}
                        exclusive
                        onChange={handleSizeChange}
                        aria-label="size"
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 1,
                          "& .MuiToggleButtonGroup-grouped": {
                            margin: 0,
                          },
                        }}
                      >
                        {sizeOptions.map((item) => (
                          <StyledToggleButton
                            key={item}
                            value={item}
                            aria-label={`size ${item}`}
                          >
                            {item}
                          </StyledToggleButton>
                        ))}
                      </ToggleButtonGroup>
                    </Box>
                  )}

                  <Box>
                    <Typography
                      sx={{
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: "0.17em",
                        textTransform: "uppercase",
                        color: "#77716a",
                        mb: 1,
                      }}
                    >
                      Wardrobe Partner
                    </Typography>

                    <Button
                      onClick={handleOwnerClick}
                      disabled={!owner?.userId}
                      startIcon={<PersonOutlineOutlinedIcon />}
                      sx={{
                        padding: 0,
                        minWidth: 0,
                        color: "#171717",
                        fontFamily: "Georgia, serif",
                        fontSize: 18,
                        fontWeight: 400,
                        textTransform: "none",
                        justifyContent: "flex-start",
                        "&:hover": {
                          background: "transparent",
                          color: "#977743",
                        },
                      }}
                    >
                      {ownerName}
                    </Button>
                  </Box>

                  {/* {tryOnDate && (
                    <Box
                      sx={{
                        border: "1px solid #cfc5b7",
                        background: "#f1ede7",
                        p: 2.5,
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={1.5}
                        alignItems="flex-start"
                      >
                        <AccessTimeOutlinedIcon
                          sx={{
                            color: "#977743",
                            fontSize: 21,
                            mt: 0.2,
                          }}
                        />

                        <Box>
                          <Typography
                            sx={{
                              fontSize: 9,
                              fontWeight: 700,
                              letterSpacing: "0.16em",
                              textTransform: "uppercase",
                              color: "#977743",
                              mb: 0.7,
                            }}
                          >
                            Try-On Available
                          </Typography>

                          {tryOnBookingDate && (
                            <Typography
                              sx={{
                                fontSize: 13,
                                fontWeight: 600,
                              }}
                            >
                              {tryOnBookingDate}
                            </Typography>
                          )}

                          {tryOnBookingTimes.length > 0 && (
                            <Typography
                              sx={{
                                fontSize: 12,
                                color: "#67625c",
                                mt: 0.5,
                              }}
                            >
                              {tryOnBookingTimes.join(
                                " · "
                              )}
                            </Typography>
                          )}
                        </Box>
                      </Stack>
                    </Box>
                  )} */}

                  <Stack spacing={1.5}>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={handleBookNow}
                      // onClick={() => {
                      //   setBookingType("rental");
                      //   setShowBookingForm(true);
                      // }}
                      sx={{
                        minHeight: 55,
                        borderRadius: 0,
                        background: "#171717",
                        color: "#fff",
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: "0.18em",
                        textTransform: "uppercase",
                        boxShadow: "none",
                        "&:hover": {
                          background: "#977743",
                          boxShadow: "none",
                        },
                      }}
                    >
                      Book Now
                    </Button>

                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={handleBookTryOn}
                      // onClick={() => {
                      //   setBookingType("tryOn");
                      //   setShowBookingForm(true);
                      // }}
                      sx={{
                        minHeight: 55,
                        borderRadius: 0,
                        borderColor: "#171717",
                        color: "#171717",
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: "0.18em",
                        textTransform: "uppercase",
                        "&:hover": {
                          borderColor: "#977743",
                          color: "#977743",
                          background: "#f1ede7",
                        },
                      }}
                    >
                      Book a Try-On
                    </Button>
                  </Stack>

                  <Grid
                    container
                    spacing={1}
                    sx={{
                      pt: 1,
                    }}
                  >
                    <Grid item xs={4}>
                      <Box
                        sx={{
                          textAlign: "center",
                          py: 2,
                          px: 1,
                          borderTop: "1px solid #ddd7ce",
                          borderBottom: "1px solid #ddd7ce",
                        }}
                      >
                        <ShieldOutlinedIcon
                          sx={{
                            fontSize: 19,
                            color: "#977743",
                            mb: 0.7,
                          }}
                        />

                        <Typography
                          sx={{
                            fontSize: 9,
                            lineHeight: 1.4,
                            fontWeight: 700,
                            letterSpacing: "0.08em",
                            textTransform: "uppercase",
                          }}
                        >
                          Secure
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={4}>
                      <Box
                        sx={{
                          textAlign: "center",
                          py: 2,
                          px: 1,
                          borderTop: "1px solid #ddd7ce",
                          borderBottom: "1px solid #ddd7ce",
                        }}
                      >
                        <LocalShippingOutlinedIcon
                          sx={{
                            fontSize: 19,
                            color: "#977743",
                            mb: 0.7,
                          }}
                        />

                        <Typography
                          sx={{
                            fontSize: 9,
                            lineHeight: 1.4,
                            fontWeight: 700,
                            letterSpacing: "0.08em",
                            textTransform: "uppercase",
                          }}
                        >
                          Easy Rental
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={4}>
                      <Box
                        sx={{
                          textAlign: "center",
                          py: 2,
                          px: 1,
                          borderTop: "1px solid #ddd7ce",
                          borderBottom: "1px solid #ddd7ce",
                        }}
                      >
                        <CalendarMonthOutlinedIcon
                          sx={{
                            fontSize: 19,
                            color: "#977743",
                            mb: 0.7,
                          }}
                        />

                        <Typography
                          sx={{
                            fontSize: 9,
                            lineHeight: 1.4,
                            fontWeight: 700,
                            letterSpacing: "0.08em",
                            textTransform: "uppercase",
                          }}
                        >
                          Flexible
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Stack>
              </Grid>
            </Grid>
          )}

          {!showBookingForm && (
            <Box
              sx={{
                mt: {
                  xs: 8,
                  md: 12,
                },
              }}
            >
              <RelatedProducts subCategory={product.subCategory} />

              <Box
                sx={{
                  mt: {
                    xs: 7,
                    md: 10,
                  },
                }}
              >
                <ProductReview productId={productId} />
              </Box>

              <Faq/>

              <Box
                sx={{
                  mt: {
                    xs: 7,
                    md: 10,
                  },
                }}
              >
                <PopularThisWeek />
              </Box>
            </Box>
          )}
        </Container>
      </Box>

      <Footer />

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity={wishlistSeverity}
          sx={{
            width: "100%",
            borderRadius: 0,
          }}
          icon={
            wishlistSeverity === "success" ? <FavoriteBorderIcon /> : undefined
          }
        >
          {wishlistMessage}
        </Alert>
      </Snackbar>
      <PopUp
        open={showLoginPopup}
        setOpen={setShowLoginPopup}
        title="Sign In to Continue"
        description="Please sign in to your Hindustan Wardrobe account to book this outfit or schedule a try-on."
        primaryText="Sign In"
        onPrimary={handleLoginPopupPrimary}
      />
      <CommonShareDialog
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        title="Share Product"
        name={productName}
        description={description}
        image={productImages[0] || ""}
        price={rentAmount}
        location={pickupAddress}
        shareUrl={window.location.href}
      />
    </>
  );
}
